<template>
    <div class="auth">
        <div class="form-block pa-10">
            <v-row class="mx-0 mb-4" justify="center" align="center">
                <h2>{{ title }}</h2>
            </v-row>

            <v-form v-if="step === 'credentials' || step === 'loading'" @submit.prevent="check">
                <v-row class="ma-0">
                    <TextField
                        color="mainNew"
                        filled
                        v-model="host"
                        label="Адрес сервера"
                        placeholder="api.example.com"
                        :disabled="busy"
                        required
                    ></TextField>
                    <TextField
                        type="password"
                        autocomplete="off"
                        color="mainNew"
                        filled
                        v-model="token"
                        label="Токен"
                        :disabled="busy"
                        required
                    ></TextField>
                </v-row>
                <p v-if="errorText" role="alert" class="error--text">{{ errorText }}</p>
                <v-btn
                    color="mainNew"
                    class="white--text"
                    :disabled="checkDisabled"
                    :loading="step === 'loading'"
                    @click="check"
                    >Проверить</v-btn
                >
            </v-form>

            <div v-else>
                <v-radio-group v-model="salonId" label="Салон" :disabled="busy">
                    <v-radio v-for="salon in salons" :key="salon.id" :value="salon.id" color="mainNew">
                        <template v-slot:label>
                            <span>{{ salon.name }}</span>
                            <span class="grey--text ml-2">{{ salon.timezone }}</span>
                        </template>
                    </v-radio>
                </v-radio-group>
                <p v-if="step === 'connecting'" class="grey--text">
                    Подключаем салон... это может занять несколько секунд
                </p>
                <p v-if="errorText" role="alert" class="error--text">{{ errorText }}</p>
                <v-btn
                    color="mainNew"
                    class="white--text"
                    :disabled="salonId === null || busy"
                    :loading="step === 'connecting'"
                    @click="submit"
                    >Подключить</v-btn
                >
                <div class="mt-4">
                    <a href="#" :class="{ 'pointer-events-none': busy }" @click.prevent="changeToken"
                        >Изменить токен</a
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import TextField from '@/components/inputs/TextField.vue'
import { login, connect } from '@api/connectors/waitThai'
import { getWaitThaiErrorMessage } from '@/helpers/waitThai/errorMessages'
import { CONNECTOR_TYPES } from '@/vars/general'

// Токен партнёра живёт только в data компонента: не писать в Vuex, sessionStorage, localStorage, URL.
export default {
    components: { TextField },
    // В мастере онбординга (AuthorizationInCRMSystemView) провайдится onboardingWizardNav;
    // в AddConnectorStage2 его нет — там после подключения идёт router.push.
    inject: { wizardNav: { from: 'onboardingWizardNav', default: null } },
    data: () => ({
        step: 'credentials', // credentials -> loading -> salons -> connecting
        host: '',
        token: '',
        salons: [],
        salonId: null,
        connector: null,
        errorText: '',
    }),
    computed: {
        ...mapState('project', ['currentAddingConnector']),
        title() {
            // в онбординге currentAddingConnector — строка-тип, в AddConnectorStage2 — объект
            return (this.currentAddingConnector && this.currentAddingConnector.name) || 'WaitThai'
        },
        busy() {
            return this.step === 'loading' || this.step === 'connecting'
        },
        checkDisabled() {
            return this.busy || !this.host.trim() || !this.token
        },
    },
    mounted() {
        if (this.wizardNav) {
            // Форма сама ведёт «Проверить/Подключить», футер мастера: только «Назад»
            this.wizardNav.set({
                canBack: true,
                canNext: false,
                showNext: false,
                onBack: this.goBackToCrm,
            })
        }
    },
    beforeDestroy() {
        this.token = ''
        if (this.wizardNav && this.wizardNav.reset) {
            this.wizardNav.reset()
        }
    },
    methods: {
        credentials() {
            return { host: this.host.trim(), token: this.token }
        },
        async check() {
            if (this.checkDisabled) {
                return
            }
            this.step = 'loading'
            this.errorText = ''
            const projectId = this.$router.currentRoute.params.id
            if (!this.connector) {
                try {
                    this.connector = await this.$store.dispatch('project/createConnector', {
                        projectId,
                        connectorType: CONNECTOR_TYPES.waitThai,
                    })
                } catch (e) {
                    this.connector = null
                }
                if (!this.connector) {
                    this.step = 'credentials'
                    this.errorText = getWaitThaiErrorMessage('NETWORK', '')
                    return
                }
            }
            const { data, error } = await login(this.connector, this.credentials())
            if (error) {
                this.step = 'credentials'
                this.errorText = getWaitThaiErrorMessage(error.code, error.message)
                return
            }
            if (!data.salons || !data.salons.length) {
                this.step = 'credentials'
                this.errorText = 'У токена нет доступных салонов'
                return
            }
            this.salons = data.salons
            this.salonId = null
            this.step = 'salons'
        },
        async submit() {
            if (this.salonId === null || this.busy) {
                return
            }
            this.step = 'connecting'
            this.errorText = ''
            const { data, error } = await connect(this.connector, this.credentials(), this.salonId)
            if (error) {
                const backToCredentials =
                    error.code === 'WAITTHAI_INVALID_TOKEN' || error.code === 'WAITTHAI_HOST_INVALID'
                this.step = backToCredentials ? 'credentials' : 'salons'
                this.errorText = getWaitThaiErrorMessage(error.code, error.message)
                return
            }
            const projectId = this.$router.currentRoute.params.id
            this.token = ''
            this.$store.commit('addConnector', data.connector, { root: true })
            this.$store.commit('project/setLoggedAddingConnector', data.connector)
            if (this.wizardNav) {
                // Онбординг: коннектор уже создан на бэке через connect, завершаем шаг по контракту
                // AuthorizationInCRMSystemView (login -> next), без перехода из мастера.
                this.$parent.$emit('login')
                return
            }
            this.$store.dispatch('callNotify', 'WaitThai подключён')
            this.$router.push('/project/' + projectId)
        },
        goBackToCrm() {
            this.$parent.$emit('goBackToCrm')
        },
        changeToken() {
            if (this.busy) {
                return
            }
            this.salons = []
            this.salonId = null
            this.errorText = ''
            this.step = 'credentials'
        },
    },
}
</script>
<style lang="sass" scoped>
.auth
    display: flex
    justify-content: center
.form-block
    border: 1px solid #0000001F
    min-width: 360px
    min-height: 344px
    max-width: 460px
.pointer-events-none
    pointer-events: none
</style>
